import React, { useState } from 'react';
import { SectionWrapper } from './SectionWrapper';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, TrendingUp, Trophy, Star, Zap, Award, ChevronRight, CheckCircle2, Flame, Target as TargetIcon, Crown, ArrowUpRight } from 'lucide-react';
export function MerchantInterface() {
  const [selectedAmount, setSelectedAmount] = useState(40000);
  const maxOffer = 45000;
  const achievements = [{
    icon: Flame,
    title: '90-Day Streak',
    desc: 'No missed settlements',
    earned: true,
    points: 200
  }, {
    icon: TrendingUp,
    title: 'Growth Mode',
    desc: '+15% GMV vs last quarter',
    earned: true,
    points: 350
  }, {
    icon: Star,
    title: 'Customer Hero',
    desc: '0 chargebacks in 60 days',
    earned: true,
    points: 150
  }, {
    icon: Crown,
    title: 'Diversified',
    desc: '50+ unique customers/mo',
    earned: false,
    points: 250
  }, {
    icon: Award,
    title: 'Top-Up Champion',
    desc: 'Repaid 60%+ early twice',
    earned: false,
    points: 400
  }];
  const tiers = [{
    name: 'Bronze',
    factor: 1.22,
    color: 'text-orange-600 bg-orange-50 border-orange-200',
    current: false
  }, {
    name: 'Silver',
    factor: 1.18,
    color: 'text-slate-600 bg-slate-100 border-slate-300',
    current: false
  }, {
    name: 'Gold',
    factor: 1.15,
    color: 'text-amber-700 bg-amber-50 border-amber-300',
    current: true
  }, {
    name: 'Platinum',
    factor: 1.11,
    color: 'text-emerald-700 bg-emerald-50 border-emerald-300',
    current: false
  }];
  return <SectionWrapper title="Merchant Interface" subtitle="The merchant-facing experience inside the Pomelo app. Loan offers, top-ups, and gamified rewards keep merchants engaged and motivated to maintain healthy business signals." data-id="element-459">
      <div className="space-y-8" data-id="element-460">
        {/* Mock Phone Mockup — Loan Offer */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-461">
          <div data-id="element-462">
            <div className="flex items-center gap-2 mb-3" data-id="element-463">
              <Sparkles className="w-4 h-4 text-emerald-600" data-id="element-464" />
              <h2 className="text-lg font-semibold text-slate-900" data-id="element-465">
                Pre-Qualified Loan Offer
              </h2>
            </div>
            <p className="text-slate-600 text-sm leading-relaxed mb-5" data-id="element-466">
              Merchants receive a personalised, fully-priced offer in their
              Pomelo dashboard the moment they cross the eligibility threshold.
              No application form — accept in one tap.
            </p>
            <ul className="space-y-3" data-id="element-467">
              {['Offers refresh every 30 days based on the latest PSP signals', 'Pricing is locked at acceptance — no surprises', 'Funds disbursed to settlement account within 24 hours', 'Repayment automatic — nothing to remember'].map((item, i) => <li key={i} className="flex items-start gap-3 text-sm text-slate-700" data-id="element-468">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" data-id="element-469" />
                  <span data-id="element-470">{item}</span>
                </li>)}
            </ul>
          </div>

          {/* Phone-style offer card */}
          <div className="bg-slate-900 rounded-3xl p-2 shadow-2xl mx-auto w-full max-w-sm" data-id="element-471">
            <div className="bg-gradient-to-b from-emerald-200 to-emerald-50 rounded-[20px] overflow-hidden" data-id="element-472">
              {/* Header */}
              <div className="bg-emerald-300 p-5 flex items-center justify-between" data-id="element-473">
                <div className="flex items-center gap-2" data-id="element-474">
                  <div className="w-7 h-7 rounded-md bg-black flex items-center justify-center text-emerald-300 font-bold text-sm" data-id="element-475">
                    P
                  </div>
                  <span className="font-semibold text-slate-900 text-sm" data-id="element-476">
                    Pomelo Capital
                  </span>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider bg-black text-emerald-300 px-2 py-1 rounded" data-id="element-477">
                  New
                </span>
              </div>

              {/* Offer body */}
              <div className="p-6 bg-white" data-id="element-478">
                <p className="text-xs uppercase tracking-widest text-emerald-700 font-bold mb-2" data-id="element-479">
                  You're pre-approved
                </p>
                <h3 className="text-2xl font-bold text-slate-900 mb-1" data-id="element-480">
                  Borrow up to
                </h3>
                <div className="flex items-baseline gap-1 mb-5" data-id="element-481">
                  <span className="text-4xl font-bold text-slate-900 font-mono" data-id="element-482">
                    SGD {maxOffer.toLocaleString()}
                  </span>
                </div>

                {/* Slider */}
                <div className="mb-5" data-id="element-483">
                  <div className="flex justify-between text-xs text-slate-500 mb-1.5" data-id="element-484">
                    <span data-id="element-485">Choose amount</span>
                    <span className="font-mono font-semibold text-slate-900" data-id="element-486">
                      SGD {selectedAmount.toLocaleString()}
                    </span>
                  </div>
                  <input type="range" min={5000} max={maxOffer} step={1000} value={selectedAmount} onChange={e => setSelectedAmount(Number(e.target.value))} className="w-full accent-emerald-500" data-id="element-487" />
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-3 mb-5" data-id="element-488">
                  <div className="bg-slate-50 rounded-lg p-3" data-id="element-489">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold" data-id="element-490">
                      Total Repayment
                    </p>
                    <p className="font-mono font-bold text-slate-900 text-sm mt-0.5" data-id="element-491">
                      SGD {Math.round(selectedAmount * 1.15).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3" data-id="element-492">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold" data-id="element-493">
                      Holdback
                    </p>
                    <p className="font-mono font-bold text-slate-900 text-sm mt-0.5" data-id="element-494">
                      10% daily
                    </p>
                  </div>
                </div>

                <button className="w-full bg-black text-emerald-300 font-bold py-3.5 rounded-xl text-sm flex items-center justify-center gap-2 hover:bg-slate-800 transition-colors" data-id="element-495">
                  Accept & Get Funded
                  <ArrowUpRight className="w-4 h-4" data-id="element-496" />
                </button>
                <p className="text-[10px] text-center text-slate-400 mt-3" data-id="element-497">
                  Funds arrive within 24 hours · Pricing locked at acceptance
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Top-Up Progress */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8" data-id="element-498">
          <div className="flex items-center gap-2 mb-6" data-id="element-499">
            <Zap className="w-5 h-5 text-emerald-600" data-id="element-500" />
            <h2 className="text-lg font-semibold text-slate-900" data-id="element-501">
              Top-Up Progress
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center" data-id="element-502">
            <div className="lg:col-span-2" data-id="element-503">
              <div className="flex justify-between text-sm mb-2" data-id="element-504">
                <span className="text-slate-600" data-id="element-505">Repaid so far</span>
                <span className="font-mono font-semibold text-slate-900" data-id="element-506">
                  SGD 31,280 / 46,000
                </span>
              </div>
              <div className="relative h-4 bg-slate-100 rounded-full overflow-hidden" data-id="element-507">
                <motion.div initial={{
                width: 0
              }} animate={{
                width: '68%'
              }} transition={{
                duration: 1,
                ease: 'easeOut'
              }} className="absolute inset-y-0 left-0 bg-emerald-400 rounded-full" data-id="element-508" />
                <div className="absolute top-0 left-[60%] h-full w-0.5 bg-slate-900" data-id="element-509" />
                <div className="absolute -top-6 left-[60%] -translate-x-1/2 text-[10px] font-semibold text-slate-900 whitespace-nowrap" data-id="element-510">
                  60% unlocks top-up
                </div>
              </div>
              <div className="flex justify-between text-xs text-slate-500 mt-2" data-id="element-511">
                <span data-id="element-512">Started · Jan 2026</span>
                <span className="text-emerald-700 font-semibold" data-id="element-513">
                  68% repaid
                </span>
                <span data-id="element-514">Projected close · Oct 2026</span>
              </div>
            </div>

            <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-200" data-id="element-515">
              <div className="flex items-center gap-2 mb-2" data-id="element-516">
                <Sparkles className="w-4 h-4 text-emerald-700" data-id="element-517" />
                <span className="text-xs font-bold uppercase tracking-wider text-emerald-700" data-id="element-518">
                  Top-up unlocked
                </span>
              </div>
              <p className="text-2xl font-bold text-slate-900 font-mono mb-1" data-id="element-519">
                SGD 52,000
              </p>
              <p className="text-xs text-slate-600 mb-4" data-id="element-520">
                Re-modelled offer based on your last 90 days. Score improved
                from 81.7 → 84.2.
              </p>
              <button className="w-full bg-black text-emerald-300 font-semibold py-2.5 rounded-lg text-sm hover:bg-slate-800 transition-colors flex items-center justify-center gap-2" data-id="element-521">
                See new offer <ChevronRight className="w-4 h-4" data-id="element-522" />
              </button>
            </div>
          </div>
        </div>

        {/* Gamification */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-xl overflow-hidden" data-id="element-523">
          <div className="p-6 md:p-8 border-b border-slate-700/50" data-id="element-524">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-525">
              <div data-id="element-526">
                <div className="flex items-center gap-2 mb-2" data-id="element-527">
                  <Trophy className="w-5 h-5 text-amber-400" data-id="element-528" />
                  <span className="text-xs font-bold uppercase tracking-widest text-amber-400" data-id="element-529">
                    Pomelo Rewards
                  </span>
                </div>
                <h2 className="text-2xl font-bold text-white" data-id="element-530">
                  Better business → Better rates
                </h2>
                <p className="text-slate-400 text-sm mt-2 max-w-lg" data-id="element-531">
                  Earn points by maintaining healthy business signals. Every
                  tier reduces your pricing on the next loan or top-up.
                </p>
              </div>
              <div className="flex-shrink-0 bg-slate-950/50 rounded-xl px-5 py-3 border border-slate-700" data-id="element-532">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold" data-id="element-533">
                  Your points
                </p>
                <p className="text-3xl font-bold text-emerald-300 font-mono" data-id="element-534">
                  700
                </p>
              </div>
            </div>
          </div>

          {/* Tier Ladder */}
          <div className="p-6 md:p-8 border-b border-slate-700/50" data-id="element-535">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-4" data-id="element-536">
              Rate Ladder
            </p>
            <div className="grid grid-cols-4 gap-2" data-id="element-537">
              {tiers.map(tier => <div key={tier.name} className={`relative rounded-lg p-3 text-center border-2 ${tier.current ? 'border-amber-400 bg-amber-400/10' : 'border-slate-700 bg-slate-800/50'}`} data-id="element-538">
                  {tier.current && <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-amber-400 text-slate-900 text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-539">
                      You
                    </span>}
                  <p className={`text-xs font-bold uppercase tracking-wider ${tier.current ? 'text-amber-300' : 'text-slate-400'}`} data-id="element-540">
                    {tier.name}
                  </p>
                  <p className={`font-mono text-lg font-bold mt-1 ${tier.current ? 'text-white' : 'text-slate-500'}`} data-id="element-541">
                    {tier.factor}×
                  </p>
                </div>)}
            </div>
            <div className="mt-4 flex items-center gap-2 text-xs text-emerald-300" data-id="element-542">
              <TargetIcon className="w-3.5 h-3.5" data-id="element-543" />
              <span data-id="element-544">
                Earn <strong className="font-bold" data-id="element-545">250 more points</strong> to
                unlock Platinum and save{' '}
                <strong className="font-bold" data-id="element-546">SGD 1,840</strong> on your next
                SGD 46k loan.
              </span>
            </div>
          </div>

          {/* Achievements */}
          <div className="p-6 md:p-8" data-id="element-547">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-4" data-id="element-548">
              Achievements
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-549">
              {achievements.map((a, i) => {
              const Icon = a.icon;
              return <motion.div key={i} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: i * 0.06
              }} className={`p-4 rounded-lg border flex items-center gap-3 ${a.earned ? 'bg-emerald-400/10 border-emerald-400/30' : 'bg-slate-800/40 border-slate-700 opacity-60'}`} data-id="element-550">
                    <div className={`p-2 rounded-lg ${a.earned ? 'bg-emerald-400/20 text-emerald-300' : 'bg-slate-700 text-slate-500'}`} data-id="element-551">
                      <Icon className="w-4 h-4" data-id="element-552" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-553">
                      <p className={`text-sm font-semibold ${a.earned ? 'text-white' : 'text-slate-400'}`} data-id="element-554">
                        {a.title}
                      </p>
                      <p className="text-xs text-slate-500 truncate" data-id="element-555">
                        {a.desc}
                      </p>
                    </div>
                    <span className={`text-xs font-mono font-bold ${a.earned ? 'text-emerald-300' : 'text-slate-600'}`} data-id="element-556">
                      +{a.points}
                    </span>
                  </motion.div>;
            })}
            </div>
          </div>

          {/* Why this works */}
          <div className="px-6 md:px-8 pb-6 md:pb-8" data-id="element-557">
            <div className="bg-emerald-400/5 border border-emerald-400/20 rounded-lg p-4 flex items-start gap-3" data-id="element-558">
              <Sparkles className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-559" />
              <p className="text-xs text-emerald-100/80 leading-relaxed" data-id="element-560">
                <strong className="text-emerald-300" data-id="element-561">
                  Aligned incentives.
                </strong>{' '}
                Behaviours that earn points — low chargebacks, customer
                diversity, growth, on-time settlement — are the same signals
                that reduce credit risk. Merchants get cheaper capital. Pomelo
                and the bank get a healthier book.
              </p>
            </div>
          </div>
        </div>
      </div>
    </SectionWrapper>;
}